import {
  UpcomingEventCreateDocument,
  UpcomingEventDeleteDocument,
  UpcomingEventDocumentDownloadDocument,
  UpcomingEventDocumentUploadDocument,
  UpcomingEventForHomepageByIdDocument,
  UpcomingEventGetByIdDocument,
  UpcomingEventListTimeZonesDocument,
  UpcomingEventsByUserDocument,
  UpcomingEventsPaginatedDocument,
  UpcomingEventUpdateDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { IFile } from "api/services/types";
import { Optional, PartialNullable } from "config";
import { executeEviction } from "api/utils/cache-eviction";
import { client } from "config/apollo";

import {
  GetUpcomingEventsReturnType,
  ICreateUpcomingEventVars,
  ITimezoneModel,
  IUpcomingEventDocumentModel,
  IUpcomingEventModel,
  IUpcomingEventsPaginatedResponse,
  IUpcomingEventsPaginatedVars,
  IUpdateUpcomingEventVars,
  UpcomingEventCreateUpdateReturnType,
  UpcomingEventsForHomepageResponse,
} from "./types";

export async function getUpcomingEventsPaginated(
  variables: IUpcomingEventsPaginatedVars
): Promise<IUpcomingEventsPaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UpcomingEventsPaginatedDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.upcomingEventsPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createUpcomingEvent(
  variables: ICreateUpcomingEventVars
): Promise<Optional<UpcomingEventCreateUpdateReturnType>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: UpcomingEventCreateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    executeEviction(client, {
      typename: "UpcomingEvent",
      fieldNames: ["upcomingEventsPaginated", "upcomingEventsByUser"],
    });

    return res?.data?.upcomingEventCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateUpcomingEvent(
  variables: IUpdateUpcomingEventVars
): Promise<Optional<UpcomingEventCreateUpdateReturnType>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: UpcomingEventUpdateDocument,
      variables,
      fetchPolicy: "network-only",
    });

    executeEviction(client, {
      typename: "UpcomingEvent",
      id: variables.upcomingEventId.toString(),
      fieldNames: [
        "upcomingEventsPaginated",
        "upcomingEventsByUser",
        "upcomingEventGetById",
        "upcomingEventViewForInvestor",
      ],
    });

    return res?.data?.upcomingEventUpdate;
  } catch (error) {
    throw error;
  }
}

export async function getUpcomingEvent(
  upcomingEventId: number
): Promise<PartialNullable<IUpcomingEventModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UpcomingEventGetByIdDocument,
      variables: { upcomingEventId },
      fetchPolicy: "network-only",
    });

    return res?.data?.upcomingEventGetById;
  } catch (error) {
    throw error;
  }
}

export async function getUpcomingEvents(): Promise<
  GetUpcomingEventsReturnType[]
> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UpcomingEventsByUserDocument,
      fetchPolicy: "network-only",
    });

    return res?.data?.upcomingEventsByUser ?? [];
  } catch (error) {
    throw error;
  }
}

export async function deleteUpcomingEvent(
  upcomingEventId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: UpcomingEventDeleteDocument,
      variables: { upcomingEventId },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "UpcomingEvent",
      id: upcomingEventId.toString(),
      fieldNames: ["upcomingEventsPaginated", "upcomingEventsByUser"],
    });

    return Boolean(res?.data?.upcomingEventDelete);
  } catch (error) {
    throw error;
  }
}

export async function uploadUpcomingEventDocument(
  variables: IFile
): Promise<Optional<IUpcomingEventDocumentModel<number>>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: UpcomingEventDocumentUploadDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    if (variables.relatedEntityId) {
      executeEviction(client, {
        typename: "UpcomingEvent",
        id: variables.relatedEntityId.toString(),
        fieldNames: [
          "upcomingEventGetById",
          "upcomingEventViewForInvestor",
          "upcomingEventsPaginated",
          "upcomingEventsByUser",
        ],
      });
    }

    return res?.data?.upcomingEventDocumentUpload;
  } catch (error) {
    throw error;
  }
}

export async function downloadUpcomingEventDocument(
  upcomingEventDocumentId: number
): Promise<Optional<string>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: UpcomingEventDocumentDownloadDocument,
      variables: { upcomingEventDocumentId },
      fetchPolicy: "no-cache",
    });

    return res?.data?.upcomingEventDocumentDownload;
  } catch (error) {
    throw error;
  }
}

export async function getUpcomingEventForHomepage(
  upcomingEventId: number
): Promise<UpcomingEventsForHomepageResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UpcomingEventForHomepageByIdDocument,
      variables: { upcomingEventId },
      fetchPolicy: "network-only",
    });

    return res?.data?.upcomingEventViewForInvestor;
  } catch (error) {
    throw error;
  }
}

export async function getTimeZones(): Promise<ITimezoneModel[]> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: UpcomingEventListTimeZonesDocument,
      fetchPolicy: "cache-first",
    });

    return res?.data?.upcomingEventListTimeZones ?? [];
  } catch (e) {
    throw e;
  }
}
